import React, { useCallback, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment-timezone";
import bigInt from "big-integer";

import { withStyles } from "@material-ui/core/styles";
import { Typography, useTheme } from "@material-ui/core";
import { withNamespaces } from "react-i18next";
import Tooltip from "@material-ui/core/Tooltip";

import { ReactComponent as TwitterIcon } from "../../../assets/twitter.svg";
import { ReactComponent as TelegramIcon } from "../../../assets/telegram.svg";
import { ReactComponent as GithubIcon } from "../../../assets/github.svg";
import { ReactComponent as WhitepaperIcon } from "../../../assets/whitepaper.svg";

import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";

import {
  THEME_MODE_CHANGE_RETURNED,
  toFixed,
  tokens,
} from "../../../constants/constants";
import Status, { getStatusBarColor } from "../../pools/components/Status";
import store from "../../../stores/store";

const styles = (theme) => ({
  poolInfoCard: {
    height: "max-content",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",

    marginBottom: "30px",

    [theme.breakpoints.up("sm")]: {
      marginBottom: "20px",
    },
    [theme.breakpoints.up("md")]: {
      marginBottom: "22px",
    },
  },
  poolMainInfoGradientBase: {
    background:
      "linear-gradient(69deg, #003ac8 0%, #0a42d7 0%, #3a6cf3 73%, #5a84f0 100%)",
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,

    borderRadius: "30px",
    padding: "16px 24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      padding: "24px 32px",
      alignItems: "center",
      justifyContent: "space-between",
    },

    marginBottom: "16px",
    gap: "16px",
    [theme.breakpoints.up("sm")]: {
      gridGap: "20px",
      marginBottom: "20px",
    },
    [theme.breakpoints.up("md")]: {
      gridGap: "22px",
      marginBottom: "22px",
    },
  },
  poolMainInfoGradientPulse: {
    background:
      "linear-gradient(69deg, #ff0000bc 0%, #F000F1bc 0%, #5521FFbc 73%, #00EEFFbc 100%)",
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,

    borderRadius: "30px",
    padding: "16px 24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      padding: "24px 32px",
      alignItems: "center",
      justifyContent: "space-between",
    },

    marginBottom: "16px",
    gap: "16px",
    [theme.breakpoints.up("sm")]: {
      gridGap: "20px",
      marginBottom: "20px",
    },
    [theme.breakpoints.up("md")]: {
      gridGap: "22px",
      marginBottom: "22px",
    },
  },

  poolMainInfo: {
    backgroundColor: "#fff",
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,

    borderRadius: "30px",
    padding: "16px 24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexDirection: "column",

    [theme.breakpoints.up("sm")]: {
      padding: "24px 32px",
      alignItems: "center",
      justifyContent: "space-between",
    },

    marginBottom: "30px",
    gap: "16px",
    [theme.breakpoints.up("sm")]: {
      gridGap: "20px",
      marginBottom: "20px",
    },
    [theme.breakpoints.up("md")]: {
      gridGap: "22px",
      marginBottom: "22px",
    },
  },
  poolTitleAddressContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
    },
  },
  poolImage: {
    width: "90px",
    height: "90px",
    marginRight: "15px",
    [theme.breakpoints.up("sm")]: {
      marginRight: "35px",
    },
  },
  copyIcon: {
    width: "16px",
    height: "16px",
    cursor: "pointer",

    color: theme?.colors?.presale?.icon,
    "&:hover": {
      opacity: 0.8,
    },
  },

  poolTitleAddress: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  poolTitle: {
    color: theme?.colors?.presale?.cardTitle,
    fontSize: "20px",
    fontWeight: "bold",
    lineHeight: "24px",
    marginBottom: "5px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "30px",
      lineHeight: "36px",
      marginBottom: "10px",
    },
    position: "relative",
  },
  contactTitle: {
    color: theme?.colors?.presale?.cardTitle,
    fontSize: "14px",
    fontWeight: "bold",
    [theme.breakpoints.up("sm")]: {
      fontSize: "22px",
    },
  },

  poolTokenLabel: {
    color: theme?.colors?.presale?.cardText,
    fontSize: "15px",
    fontWeight: "normal",
    lineHeight: "18px",
    marginBottom: "8px",
  },
  fundraiseGoal: {
    color: theme?.colors?.presale?.fundraiseGoalTextPrimary,
    fontWeight: "600",

    fontSize: "14px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "16px",
    },
  },

  poolStatusContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  poolStatusBox: {
    minWidth: "140px",
    width: "fit-content",
    padding: "2px 13px",
    borderRadius: "24px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme?.colors.poolCard?.poolTypeText,
    whiteSpace: "nowrap",

    fontSize: "13px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "15px",
    },
  },
  poolListingInfo: {
    marginTop: "25px",
    color: theme?.colors?.presale?.cardText,
    fontSize: "15px",
    fontWeight: "normal",
    lineHeight: "18px",
  },
  poolStatusInfo: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  infoCard: {
    backgroundColor: theme?.colors?.presale?.cardBackground,
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "30px",
    padding: "16px 24px",
    [theme.breakpoints.up("sm")]: {
      padding: "24px 32px",
    },
  },
  poolSaleInfo: {
    display: "flex",
    gap: "4px",
    alignItems: "flex-end",
    width: "100%",
    marginBottom: "4px",
  },
  poolSaleText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
  },
  poolSaleHeader: {
    color: theme?.colors?.presale?.cardText,
    fontSize: "15px",
    fontWeight: "normal",
    lineHeight: "18px",
    marginBottom: "8px",
  },
  poolSaleValue: {
    color: theme?.colors?.presale?.addressValueText,
    fontSize: "25px",
    fontWeight: "normal",
    lineHeight: "30px",
  },
  poolPercentageBox: {
    display: "flex",
    height: "12px",
    width: "100%",
    backgroundColor: theme?.colors?.presale?.percentageBarBackground,
    borderRadius: "12px",
    marginBottom: "4px",
    position: "relative",
    [theme.breakpoints.up("sm")]: {
      height: "16px",
      marginBottom: "8px",
    },
  },
  poolFilledBox: {
    height: "12px",
    width: "100%",
    backgroundColor: theme?.colors?.presale?.addressValueText + " !important",
    borderRadius: "12px",
    position: "absolute",
    left: "0px",
    top: "0px",
    [theme.breakpoints.up("sm")]: {
      height: "16px",
    },
  },
  poolPercentageInfo: {
    display: "flex",
    width: "100%",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  poolPercentageText: {
    color: theme?.colors?.presale?.fundraiseGoalTextSecondary,
    fontSize: "15px",
    fontWeight: "600",
    [theme.breakpoints.up("sm")]: {
      fontSize: "25px",
    },
  },
  poolPercentageTotalGoalText: {
    color: theme?.colors?.presale?.fundraiseGoalTextPrimary,
    fontSize: "20px",
    fontWeight: "600",
    lineHeight: "normal",
    [theme.breakpoints.up("sm")]: {
      fontSize: "35px",
    },
  },
  participantsText: {
    color: theme?.colors?.presale?.fundraiseGoalTextPrimary,

    fontWeight: "600",

    lineHeight: "18px",
    fontSize: "13px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "15px",
    },
  },
  poolSubInfoSecondaryContainer: {
    display: "grid",
    width: "100%",
    gridGap: "15px",
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gridGap: "20px",
    },
    [theme.breakpoints.up("ms")]: {
      gridTemplateColumns: "1fr 1fr 1fr",
      gridGap: "25px",
    },
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr 1fr 1fr 1fr",
      gridGap: "30px",
    },
  },
  poolSubInfoContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "30px",

    [theme.breakpoints.up("sm")]: {
      gridGap: "20px",
    },

    [theme.breakpoints.up("md")]: {
      gridGap: "22px",
    },
  },

  poolSubInfoV3Container: {
    display: "grid",
    width: "100%",
    gridGap: "15px",
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gridGap: "20px",
    },
    [theme.breakpoints.up("ms")]: {
      gridTemplateColumns: "1fr 1fr 1fr",
      gridGap: "25px",
    },
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr 1fr 1fr 1fr",
      gridGap: "30px",
    },
  },
  poolSubInfoCard: {
    display: "flex",
    width: "100%",
    gap: "5px",

    [theme.breakpoints.down("sm")]: {
      alignItems: "center",
    },
  },
  contactPoolSubInfoCard: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "14px",
  },
  dotSeparator: {
    width: "100%",
    flex: "1",
    borderBottom: "2px dotted " + theme?.colors?.presale?.divider,
    marginBottom: "6px",
  },
  lineSeparator: {
    width: "100%",
    borderBottom: "2px solid " + theme?.colors?.presale?.divider,
    margin: "8px 0",

    [theme.breakpoints.up("md")]: {
      margin: "14px 0",
    },
  },
  connectContainer: {
    display: "flex",
    alignItems: "center",
    height: "32px",
    justifyContent: "flex-start",
    gap: "8px",
    [theme.breakpoints.up("md")]: {
      gap: "16px",
    },
  },
  icon: {
    color: theme?.colors?.presale?.icon,
    width: "32px",
    height: "32px",
  },
  linkWebsite: {
    color: theme?.colors?.presale?.linkText,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      opacity: 0.8,
    },
  },
  iconLink: {
    textDecoration: "none",
    height: "32px",
    width: "32px",
    "&:hover": {
      opacity: 0.8,
    },
  },
  poolInfoHeading: {
    color: theme?.colors?.presale?.cardText,
    fontWeight: "normal",
    lineHeight: "normal",
    marginBottom: "0px",
    cursor: "default",

    fontSize: "14px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "115px",
    },
  },

  poolSubInfoHeading: {
    color: theme?.colors?.presale?.cardText,
    fontWeight: "normal",
    lineHeight: "normal",
    marginBottom: "0px",
    cursor: "default",

    fontSize: "14px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "16px",
    },
    transition: "all 0.3s ease",

    "&:hover": {
      textDecoration: "underline",
      color: theme?.colors?.home?.infoBoxDescription,
    },
  },

  poolSubInfoValue: {
    color: theme?.colors?.presale?.cardText,
    fontWeight: "600",
    lineHeight: "normal",
    textAlign: "right",
    fontSize: "13px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "15px",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "140px",
    },
  },
  link: {
    textDecoration: "none",
    color: theme?.colors?.presale?.linkText,
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "normal",
    "&:hover": {
      opacity: 0.8,
    },
  },
  badge: {
    position: "absolute",
    right: "-40px",
    width: "30px",
    height: "30px",
  },
  tooltip: {
    fontSize: "16px",
  },
});

const Store = store.store;
const emitter = store.emitter;

const infoDescriptions = {
  presaleType:
    "Sale Type: Includes Token Sale, Public IDO, Degen, and Certified BUIDLer options, each with specific requirements and features.",
  guaranteedHours:
    "Headstart: The time to the guaranteed allocation round to buy first.",
  categoryId: "Category: The category of a project.",
  openTime:
    "Open Time: The date and time a presale will begin accepting investor funds.",
  closeTime:
    "Close Time: The date and time a presale will stop accepting investor funds.",
  softCapInWei:
    "SoftCap: The minimum amount of {{ FUNDING_TOKEN_NAME }} required from a pool to be launched on Starter.",
  hardCapInWei:
    "HardCap: The total amount of {{ FUNDING_TOKEN_NAME }} that can be pooled before launch.",
  minInvestInWei:
    "Min Per Wallet: The minimum amount of {{ FUNDING_TOKEN_NAME }} that each investor may contribute to a presale.",
  tokenPriceInWei:
    "Token Price: The price of a token per unit in {{ FUNDING_TOKEN_NAME }}.",

  yesVotes:
    "Yes Votes: represent the total affirmative votes a project receives, where each staked {{ STAKING_TOKEN_NAME }} equals one vote, contributing towards the required 20,000,000 net YES votes for listing.",
  noVotes:
    "No Votes: signify the total negative votes cast against a project, with each staked {{ STAKING_TOKEN_NAME }} counting as one NO vote, directly reducing the project's net YES vote tally.",
  releasePerCycle:
    "Release Per Cycle: The percentage of tokens that will be claimable by investors every cycle.",
  releaseCycle:
    "Release Cycle: The period of time in seconds that tokens can be claimed.",
  allocationFactor:
    "Allocation Factor: This is the formula that works out how much of a token investors will be able to buy during IDO, based on how much {{ STAKING_TOKEN_NAME }} they have staked. ",
  maxPerWallet:
    "Max Per Wallet: The maximum amount of {{ FUNDING_TOKEN_NAME }} that each investor may contribute to a presale.",
  maxInvestAmount:
    "Maximum Investment: The maximum investment allowed for an investor.",
  releaseAtTGE: "",

  cakeLiquidityPercentageAllocation:
    "DEX Liquidity Pool (LP) Allocation Percent: The percentage of the raised funds that will be used for the LP on a DEX.",
  cakeLPTokensLockDurationInDays:
    "DEX Liquidity Pool (LP) lock duration days: The number of days that a liquidity will be locked in the LP on the DEX.",
  cakeListingPriceInWei:
    "DEX Listing Price: The listing price of a token in {{ FUNDING_TOKEN_NAME }} when it is available to trade on a DEX.",
  cakeLiquidityAddingTime:
    "DEX Listing Time: The date and time when a LP on the DEX will be available for trading.",
  swapIndex:
    "DEX: The Decentralized Exchange where the token will be available for trading.",
};

const InfoRightCards = ({ classes, poolInfo }) => {
  const fundingTokenDecimals =
    tokens[poolInfo?.fundingTokenAddress]?.decimals ?? 18;

  const [currentThemeMode, setCurrentThemeMode] = useState(
    Store.getStore("themeMode")
  );
  const theme = useTheme();

  const themeModeChanged = useCallback((newThemeMode) => {
    setCurrentThemeMode(newThemeMode);
  }, []);

  useEffect(() => {
    emitter.on(THEME_MODE_CHANGE_RETURNED, (e) => {
      themeModeChanged(e);
    });

    return () => {
      emitter.on(THEME_MODE_CHANGE_RETURNED, (e) => {
        themeModeChanged(e);
      });
    };
  }, [themeModeChanged]);

  const fundingToken = tokens[poolInfo.fundingTokenAddress];

  const shortAddress = (address) => {
    return (
      address.substring(0, 6) + "…" + address.substring(address.length - 4)
    );
  };

  const linkAddressPart = (heading, value, link) => {
    return (
      <div className={classes.poolSubInfoCard}>
        <Typography variant="h5" className={classes.poolInfoHeading}>
          {heading}
        </Typography>
        <div className={classes.dotSeparator} />

        <a
          href={link}
          className={classes.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {shortAddress(value)}
        </a>

        <FileCopyOutlined
          onClick={() => {
            navigator.clipboard.writeText(value);
          }}
          className={classes.copyIcon}
        />
      </div>
    );
  };

  const poolSubInfo = (title, info, type = "string", decimals = 6) => {
    let poolInfoItem = poolInfo[info] || 0;
    if (type === "start") {
      poolInfoItem =
        toFixed(bigInt(poolInfoItem), fundingTokenDecimals, decimals) +
        " " +
        poolInfo.stakingTokenName;
    }
    if (type === "votes") {
      poolInfoItem =
        toFixed(bigInt(poolInfoItem), fundingTokenDecimals, decimals) +
        " " +
        poolInfo.stakingTokenName;
    }
    if (type === "wei") {
      poolInfoItem =
        toFixed(bigInt(poolInfoItem), fundingTokenDecimals, decimals) +
        " " +
        fundingToken?.label;
    }
    if (type === "headstart") {
      poolInfoItem = parseInt(poolInfoItem) / 60 + " mins";
    }
    if (type === "days") {
      if (parseInt(poolInfoItem) > 1) {
        poolInfoItem = Number(poolInfoItem) / 86400 + " days";
      } else {
        poolInfoItem = Number(poolInfoItem) / 86400 + " day";
      }
    }
    if (type === "percent") {
      poolInfoItem = Number(poolInfoItem).toFixed(2) + "%";
    }
    if (type === "time") {
      poolInfoItem = moment.unix(poolInfoItem).format("MMMM DD, YYYY HH:mm A");
    }
    if (type === "cycle") {
      poolInfoItem = parseInt(poolInfoItem);
      if (poolInfoItem >= 3600 * 24) {
        if (poolInfoItem / (3600 * 24) > 1) {
          poolInfoItem = parseFloat(poolInfoItem / (3600 * 24)) + " days";
        } else {
          poolInfoItem = parseFloat(poolInfoItem / (3600 * 24)) + " day";
        }
      } else if (poolInfoItem >= 3600) {
        poolInfoItem = parseFloat(poolInfoItem / 3600) + " hours";
      } else if (poolInfoItem >= 60) {
        poolInfoItem = parseFloat(poolInfoItem / 60) + " minutes";
      } else {
        poolInfoItem = poolInfoItem + " seconds";
      }
    }
    if (type === "presaleType") {
      const pTypes = ["Token Sale", "Community IDO", "Degen Mode"];
      poolInfoItem = pTypes[poolInfoItem];
    }
    if (type === "category") {
      const categories = [
        "DeFi",
        "Farming",
        "Games",
        "Gambling",
        "Exchanges",
        "Collectibles",
        "Marketplaces",
        "Social",
        "Other",
      ];
      poolInfoItem = categories[poolInfoItem];
    }
    if (type === "dex") {
      const dexes = ["Aerodrome", "Uniswap"];

      poolInfoItem = dexes[Number(poolInfoItem)];
    }
    if (type === "releaseCycles") {
      const releaseCycles = [
        {
          label: "Daily",
          value: 86400,
        },
        {
          label: "Weekly",
          value: 604800,
        },
        {
          label: "Bi-Monthly",
          value: 1296000,
        },
        {
          label: "Monthly",
          value: 2592000,
        },
        {
          label: "Quarterly",
          value: 7776000,
        },
        {
          label: "Bi-Annually",
          value: 15552000,
        },
        {
          label: "Yearly",
          value: 31536000,
        },
      ];
      poolInfoItem = releaseCycles?.find((cycle) =>
        bigInt(poolInfoItem).eq(cycle.value)
      )?.label;
    }

    if (info === "maxInvestInWei") {
      if (poolInfo.presaleType === "1") {
        info = "allocationFactor";
      } else {
        info = "maxPerWallet";
      }
    }
    const description = infoDescriptions[info]
      ?.replace(/{{ STAKING_TOKEN_NAME }}/g, poolInfo.stakingTokenName)
      ?.replace(
        /{{ FUNDING_TOKEN_NAME }}/g,
        tokens[poolInfo?.fundingTokenAddress].label
      );

    return (
      <div className={classes.poolSubInfoCard}>
        <Tooltip
          title={description || ""}
          classes={{ tooltip: classes.tooltip }}
          placement="bottom"
        >
          <Typography variant="h5" className={classes.poolSubInfoHeading}>
            {title}
          </Typography>
        </Tooltip>
        <div className={classes.dotSeparator} />
        <Typography variant="h5" className={classes.poolSubInfoValue}>
          {poolInfoItem}
        </Typography>
      </div>
    );
  };

  const totalCollected = parseFloat(
    toFixed(bigInt(poolInfo?.totalCollectedWei || "0"), fundingTokenDecimals, 3)
  );
  const hardCap = parseFloat(
    toFixed(bigInt(poolInfo?.hardCapInWei || "0"), fundingTokenDecimals, 3)
  );

  const percentage = parseFloat((totalCollected / hardCap) * 100);

  const fillBarColorId = getStatusBarColor(poolInfo, theme);

  return (
    <div className={classes.poolInfoCard}>
      <div
        className={
          theme?.type === "base"
            ? currentThemeMode === "dark"
              ? classes.poolMainInfoGradientBase
              : classes.poolMainInfo
            : classes.poolMainInfoGradientPulse
        }
      >
        <div className={classes.poolStatusContainer}>
          <Typography variant="h5" className={classes.fundraiseGoal}>
            Fundraise Goal
          </Typography>

          <Status poolInfo={poolInfo} />
        </div>
        <div className={classes.poolStatusInfo}>
          <div className={classes.poolSaleInfo}>
            <Typography variant="h5" className={classes.poolPercentageText}>
              {totalCollected > hardCap ? hardCap : totalCollected}/
            </Typography>
            <Typography
              variant="h5"
              className={classes.poolPercentageTotalGoalText}
            >
              {hardCap} {fundingToken?.label}
            </Typography>
          </div>
          <div className={classes.poolPercentageBox}>
            <div
              className={classes.poolFilledBox}
              style={{
                width: percentage <= 100 ? `${percentage}%` : "100%",
                backgroundColor:
                  theme?.colors.poolCard?.statusBarFilled[fillBarColorId],
              }}
            />
          </div>
          <div>
            <Typography variant="h5" className={classes.participantsText}>
              {poolInfo?.totalInvestorsCount} Participants
            </Typography>
          </div>
        </div>
      </div>

      <div className={classes.poolSubInfoContainer}>
        <div className={classes.infoCard}>
          <div className={classes.contactPoolSubInfoCard}>
            <Typography variant="h1" className={classes.contactTitle}>
              About {poolInfo.saleTitle || "..."}
            </Typography>

            <div>
              <Typography
                variant="h5"
                className={classes.poolInfoHeading}
                style={{
                  marginBottom: "4px",
                }}
              >
                Website
              </Typography>
              <a
                href={poolInfo.linkWebsite}
                className={classes.linkWebsite}
                target="_blank"
                rel="noopener noreferrer"
              >
                {poolInfo.linkWebsite}
              </a>
            </div>

            <div>
              <Typography
                variant="h5"
                className={classes.poolInfoHeading}
                style={{
                  marginBottom: "4px",
                }}
              >
                Social Media
              </Typography>
              <div className={classes.connectContainer}>
                <a
                  href={
                    "https://t.me/" + poolInfo.linkTelegram?.replace("@", "")
                  }
                  className={classes.iconLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TelegramIcon className={classes.icon} />
                </a>
                <a
                  href={
                    "https://twitter.com/" +
                    poolInfo.linkTwitter?.replace("@", "")
                  }
                  className={classes.iconLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TwitterIcon className={classes.icon} />
                </a>

                <a
                  href={"https://github.com/" + poolInfo.linkGithub}
                  className={classes.iconLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GithubIcon className={classes.icon} />
                </a>
                <a
                  href={poolInfo.whitepaper}
                  className={classes.iconLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhitepaperIcon className={classes.icon} />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.infoCard}>
          {poolSubInfo("Sale Type", "presaleType", "presaleType")}
          {poolInfo.presaleType === "1" &&
            poolSubInfo("Headstart", "guaranteedHours", "headstart")}
          {poolSubInfo("Category", "categoryId", "category")}
          <div className={classes.lineSeparator} />
          {poolSubInfo("Open Time", "openTime", "time")}
          {poolSubInfo("Close Time", "closeTime", "time")}
          <div className={classes.lineSeparator} />
          {poolSubInfo("Softcap", "softCapInWei", "wei", 6)}
          {poolSubInfo("Hardcap", "hardCapInWei", "wei", 6)}
          <div className={classes.lineSeparator} />
          {poolSubInfo("Min Per Wallet", "minInvestInWei", "wei", 6)}
          {poolSubInfo(
            poolInfo.presaleType === "1"
              ? "Allocation Factor"
              : "Max Per Wallet",

            "maxInvestInWei",
            "wei",
            6
          )}
          <div className={classes.lineSeparator} />

          {poolInfo.releaseAtTGE === 100 ? (
            poolSubInfo("Release at TGE (%)", "releaseAtTGE", "percent", 6)
          ) : (
            <>
              {poolSubInfo("Release Cycle", "releaseCycle", "releaseCycles", 6)}
              {poolSubInfo("Release at TGE (%)", "releaseAtTGE", "percent", 6)}
              {poolSubInfo(
                "Release Per Cycle (%)",
                "releasePerCycle",
                "percent",
                6
              )}
              {poolSubInfo("Vesting Cliff (Days)", "releaseCliff", "days", 6)}
            </>
          )}

          <div className={classes.lineSeparator} />
          {poolSubInfo("Price per Token", "tokenPriceInWei", "wei", 6)}
          {poolInfo.presaleType !== "0" &&
            poolSubInfo("Listing Rate", "cakeListingPriceInWei", "wei", 6)}
          {poolInfo.presaleType !== "0" && (
            <>
              <div className={classes.lineSeparator} />

              {poolSubInfo(
                "Liquidity Allocation",
                "cakeLiquidityPercentageAllocation",
                "percent"
              )}

              {poolSubInfo(
                "Liquidity Lock Duration",
                "cakeLPTokensLockDurationInDays",
                "days"
              )}

              {poolSubInfo("Listing Date", "cakeLiquidityAddingTime", "time")}
              {poolSubInfo("DEX", "swapIndex", "dex")}
            </>
          )}
          {!poolInfo.certified && poolInfo.presaleType !== "2" && (
            <>
              <div className={classes.lineSeparator} />
              {poolSubInfo("Yes Votes", "yesVotes", "votes")}
              {poolSubInfo("No Votes", "noVotes", "votes")}
            </>
          )}
        </div>

        <div className={classes.infoCard}>
          {linkAddressPart(
            "Token Address",
            poolInfo.tokenAddress,
            theme.blockExplorer + "address/" + poolInfo.tokenAddress
          )}

          {linkAddressPart(
            "LP Token Address",
            poolInfo.lpToken,
            theme.blockExplorer + "address/" + poolInfo.lpToken
          )}

          {linkAddressPart(
            "Locked Liquidity Address",
            poolInfo.lockedLiquidityAddress || "-",
            poolInfo.lockedLiquidityAddress
              ? theme.blockExplorer +
                  "address/" +
                  poolInfo.lockedLiquidityAddress
              : theme.blockExplorer + "address/"
          )}

          {poolInfo.presaleIndex !== "0" &&
            linkAddressPart(
              "Presale Contract Address",
              poolInfo.presaleAddress,
              theme.blockExplorer + "address/" + poolInfo.presaleAddress
            )}

          {poolInfo.presaleIndex === "0" &&
            linkAddressPart(
              "Presale Contract Address",
              poolInfo.presaleAddress,
              theme.blockExplorer + "address/" + poolInfo.presaleAddress
            )}
        </div>
      </div>
    </div>
  );
};

export default withNamespaces()(withRouter(withStyles(styles)(InfoRightCards)));
